import React, { useState, useEffect, useCallback } from 'react';
import { doc, updateDoc, collection, getDocs, onSnapshot, query, where, setDoc, Timestamp, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { getStorage, ref, deleteObject, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  ModalContent,
  ModalHeader,
  ModalActions,
  FormGrid,
  FormSection,
  ApproveButton,
  CancelButton,
  ErrorMessage,
  LoadingSpinner,
  ImagePreviewContainer,
  MediaActions,
  MediaButton,
  AddMediaInput,
} from './editform.styles';

// Static list of countries
const countries = ['Uganda', 'Kenya', 'Tanzania', 'Rwanda', 'Burundi', 'South Sudan'];

const stallCategories = [
  'All Categories',
  'Boutique Businesses',
  'Electronic Shops/Stores',
  'Supermarkets',
  'Hardware stores/shops',
  'Food manufacturers & distributors',
  'Health & Wellness products distributors',
  'Hotels, Lodges and Guest House Businesses',
  'Airbnb',
  'Construction Companies',
  'Interior Designers',
  'Tailors & fashion Designers',
  'Clothes, Text tile, Shoes and Beddings Businesses',
  'Restaurants & Cafes',
  'Catering Service Providers',
  'Travel & Tour Companies',
  'Energy Suppliers (Including solar)',
  'Consultancy Firms',
  'Insurance Companies',
  'Tax Consultants',
  'Law Firms',
  'Car Hire & Transport Service Providers',
  'Retail Shops',
  'Grocery Businesses',
  'Drinks manufacturers and distributors (water, soft drinks, beer, wines, spirits and other beverages)',
  'Hung out places, Bars and Leisure Gardens',
  'Event Venues (wedding halls, Gardens & Concert venues)',
  'Recreation Centres (Sand Beaches, Leisure Parks, resorts, etc)',
  'Sports betting & gambling Companies',
  'Cinema Halls',
  'Hospitals, Clinics, Drug shops & Pharmacies',
  'Health Clubs (Fitness & Gyms, Sauna, Steam, Spa and Massage)',
  'Wholesale shops',
  'Auto Services Shops (Parts, Accessories & Repairs)',
  'Oil and Gas Companies (Including fuel stations)',
  'Movers/Removalists',
  'Recruitment Firms/Agencies (Jobs & Career Opportunities)',
  'Furniture and Metal works Businesses',
  'Home Appliance Businesses',
  'Decorations (Decor) Service Providers',
  'Gardens & Compound Designers',
  'Cleaning and Fumigation Businesses',
  'Laundry & Dry Cleaners',
  'Beauty Shops (Cosmetics, Fragrances and perfumes, Skincare & skincare products, Hair Salon & Hair-care products, Nails care and Nails care products, Makeup services)',
  'Jewellery & Gift Shops',
  'Logistics, Clearing & Forwarding Companies',
  'Telecom & Network Companies',
  'Schools, Institutes, Colleges & Universities',
  'Land and Buildings',
  'Car Dealers',
  'Importers and Exporters',
  'Audit & Accounting Firms',
  'IT Service Providers',
  'Electrical Engineering Companies',
  'Plumbing Companies',
  'Mechanical Engineering Companies',
  'Factories & Processing Companies',
  'Photocopying, Printing & Branding Companies',
  'Event Planners',
  'Sound & Production Service Providers',
  'Photography & Videography Service Providers',
  'Artists, Performers & Cultural Dancers',
  'Fishing Companies',
  'Farm Produce Suppliers',
  'Livestock & Livestock Products Suppliers',
  'General Equipment Suppliers',
  'Agro Suppliers (Seeds, Fertilizers & other Agricultural products)',
  'Poultry & Poultry Products Suppliers (Chicken, Eggs)',
  'Airline Booking and Visa Companies',
  'Other Businesses / Companies / Organisations'
];
// Maximum file size for profile photo (e.g., 2MB)
const MAX_PROFILE_PHOTO_SIZE = 2 * 1024 * 1024; 

interface Stall {
    id: string;
    stallNumber: string;
    businessName: string;
    contactPerson: string;
    businessPhone: string;
    category: string;
    email: string;
    website: string;
    actualLocation: string;
    whatsappPhone: string;
    additionalDetails: string;
    facebookLink: string;
    googleMapLocation: string;
    instagramLink: string;
    linkedinLink: string;
    stallType: string;
    subscriptionAmount: number;
    subscriptionDuration: string;
    tiktokLink: string;
    xTwitterLink: string;
    youtubeLink: string;
    businessPhotoPreview: string; // The URL of the current profile photo
    selectedCountry: string;
    selectedDistrict: string;
    selectedParish: string;
    selectedRegion: string;
    selectedSubcounty: string;
    selectedVillage: string;
    userId?: string;
    verificationStatus?: 'pending' | 'verified' | null;
    nidDocumentUrl?: string;
    titleOrLogbookDocumentUrl?: string;
  }
  
  interface StallListing {
    id: string;
    title: string;
    description: string;
    price: number | null;
    images: string[];
    videos: string[];
    mediaDetails?: any[];
  }
  
  interface AdministrativeUnit {
    id: string;
    name: string;
    country?: string;
    region?: string;
    district?: string;
    subcounty?: string;
    parish?: string;
    type: string; // Ensure your documents have a 'type' field
  }
  
  interface EditStallFormProps {
    stall: Stall;
    onSave: (updatedData: Partial<Stall>) => void;
    onCancel: () => void;
  }

interface FormMediaItem {
  id: string;
  url: string;
  file: File;
  type: 'image' | 'video';
  description: string;
  price?: number;
}

const mediaTypes = [
  {value: '', label: 'Select Media Type' },
  {value: 'item_image', label: 'Item Image (Description, Price, Image)' },
  {value: 'video', label: 'Video (Description, 1-minute Video(100MBs))' },
  {value: 'general_image', label: 'General Image (Description, Image)' },
];

const VERIFICATION_CATEGORIES = ['land', 'real_estate', 'cars', 'vehicles', 'automotive'];

const EditStallForm: React.FC<EditStallFormProps> = ({ stall, onSave, onCancel }) => {
  const [editedData, setEditedData] = useState<Partial<Stall>>(stall);
  const [listings, setListings] = useState<StallListing[]>([]);
  const [regions, setRegions] = useState<AdministrativeUnit[]>([]);
  const [districts, setDistricts] = useState<AdministrativeUnit[]>([]);
  const [subcounties, setSubcounties] = useState<AdministrativeUnit[]>([]);
  const [parishes, setParishes] = useState<AdministrativeUnit[]>([]);
  const [villages, setVillages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [locationData, setLocationData] = useState<any>(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [listingsLoaded, setListingsLoaded] = useState(false);
  const [showListings, setShowListings] = useState(false);
  
  // Add Listing State
  const [showAddListing, setShowAddListing] = useState(false);
  const [selectedMediaType, setSelectedMediaType] = useState('');
  const [itemImageDescription, setItemImageDescription] = useState('');
  const [itemImagePrice, setItemImagePrice] = useState('');
  const [videoDescription, setVideoDescription] = useState('');
  const [generalImageDescription, setGeneralImageDescription] = useState('');
  const [uploadedMedia, setUploadedMedia] = useState<FormMediaItem[]>([]);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [isCreatingListing, setIsCreatingListing] = useState(false);
  const [createListingError, setCreateListingError] = useState<string | null>(null);
  const [nidFile, setNidFile] = useState<File | null>(null);
  const [titleOrLogbookFile, setTitleOrLogbookFile] = useState<File | null>(null);
  const [stallVerification, setStallVerification] = useState<{ status: 'pending' | 'verified' | null; nidUrl: string | null; titleUrl: string | null; }>({ status: null, nidUrl: null, titleUrl: null });

  // 🆕 State for the new profile photo file
  const [selectedProfilePhoto, setSelectedProfilePhoto] = useState<File | null>(null);

  useEffect(() => {
    setEditedData(stall);
    setSelectedProfilePhoto(null);
  }, [stall]);

  // ... (Location data fetching and cascading useEffects remain the same)
  // --- (Location Data Fetching and Cascading Selects) ---

  useEffect(() => {
    const fetchLocationData = async () => {
      setLocationLoading(true);
      setLocationError(null);
      try {
        const querySnapshot = await getDocs(collection(db, 'administrativeUnits'));
        const fetchedData: any = {};
        querySnapshot.forEach(doc => {
          const data = doc.data();
          if (!data['Region'] || !data['District/City'] || !data['Subcounty/Division'] || !data['Parish/Ward'] || !data['Village']) {
            console.warn("Skipping malformed administrative unit document:", doc.id);
            return;
          }
          const region = data['Region'];
          const district = data['District/City'];
          const subcounty = data['Subcounty/Division'];
          const parish = data['Parish/Ward'];
          const villages = [data['Village']];
          if (!fetchedData[region]) {fetchedData[region] = {}; }
          if (!fetchedData[region][district]) {fetchedData[region][district] = {}; }
          if (!fetchedData[region][district][subcounty]) {fetchedData[region][district][subcounty] = {}; }
          fetchedData[region][district][subcounty][parish] = villages;
        });
        setLocationData(fetchedData);
      } catch (err: any) {
        console.error("Error fetching location data:", err);
        setLocationError('Failed to load location data. Please try again.');
      } finally {
        setLocationLoading(false);
      }
    };
    fetchLocationData();
  }, []);

  // Use useEffect to populate regions based on locationData
  useEffect(() => {
    const { selectedCountry } = editedData;
    if (selectedCountry === 'Uganda' && locationData) {
      const regionNames = Object.keys(locationData);
      const fetchedRegions = regionNames.map(name => ({
        id: name,
        name,
        type: 'region',
      })) as AdministrativeUnit[];
      setRegions(fetchedRegions);
    } else {
      setRegions([]);
    }
  }, [editedData.selectedCountry, locationData]);

  // Use useEffect to populate districts based on locationData
  useEffect(() => {
    const { selectedRegion } = editedData;
    if (selectedRegion && locationData && locationData[selectedRegion]) {
      const districtNames = Object.keys(locationData[selectedRegion]);
      const fetchedDistricts = districtNames.map(name => ({
        id: name,
        name,
        type: 'district',
      })) as AdministrativeUnit[];
      setDistricts(fetchedDistricts);
    } else {
      setDistricts([]);
    }
  }, [editedData.selectedRegion, locationData]);

  // Use useEffect to populate subcounties based on locationData
  useEffect(() => {
    const { selectedRegion, selectedDistrict } = editedData;
    if (selectedDistrict && selectedRegion && locationData && locationData[selectedRegion] && locationData[selectedRegion][selectedDistrict]) {
      const subcountyNames = Object.keys(locationData[selectedRegion][selectedDistrict]);
      const fetchedSubcounties = subcountyNames.map(name => ({
        id: name,
        name,
        type: 'subcounty',
      })) as AdministrativeUnit[];
      setSubcounties(fetchedSubcounties);
    } else {
      setSubcounties([]);
    }
  }, [editedData.selectedDistrict, editedData.selectedRegion, locationData]);

  // Use useEffect to populate parishes based on locationData
  useEffect(() => {
    const { selectedRegion, selectedDistrict, selectedSubcounty } = editedData;
    if (selectedSubcounty && selectedRegion && selectedDistrict && locationData && locationData[selectedRegion] && locationData[selectedRegion][selectedDistrict] && locationData[selectedRegion][selectedDistrict][selectedSubcounty]) {
      const parishNames = Object.keys(locationData[selectedRegion][selectedDistrict][selectedSubcounty]);
      const fetchedParishes = parishNames.map(name => ({
        id: name,
        name,
        type: 'parish',
      })) as AdministrativeUnit[];
      setParishes(fetchedParishes);
    } else {
      setParishes([]);
    }
  }, [editedData.selectedSubcounty, editedData.selectedRegion, editedData.selectedDistrict, locationData]);

  // Use useEffect to populate villages based on locationData
  useEffect(() => {
    const { selectedRegion, selectedDistrict, selectedSubcounty, selectedParish } = editedData;
    if (selectedParish && selectedRegion && selectedDistrict && selectedSubcounty && locationData && locationData[selectedRegion] && locationData[selectedRegion][selectedDistrict] && locationData[selectedRegion][selectedDistrict][selectedSubcounty] && locationData[selectedRegion][selectedDistrict][selectedSubcounty][selectedParish]) {
      const villageNames = locationData[selectedRegion][selectedDistrict][selectedSubcounty][selectedParish];
      setVillages(villageNames);
    } else {
      setVillages([]);
    }
  }, [editedData.selectedParish, editedData.selectedRegion, editedData.selectedDistrict, editedData.selectedSubcounty, locationData]);

  // --- (End of Location Data Fetching and Cascading Selects) ---

  // Fetch stall verification details
  useEffect(() => {
    const fetchStallDetails = async () => {
        if (stall.id) {
            const stallDocRef = doc(db, 'stalls', stall.id);
            const stallSnap = await getDoc(stallDocRef);
            if (stallSnap.exists()) {
                const data = stallSnap.data();
                setStallVerification({
                    status: data.verificationStatus || null,
                    nidUrl: data.nidDocumentUrl || null,
                    titleUrl: data.titleOrLogbookDocumentUrl || null,
                });
                // Ensure we have userId
                if (!editedData.userId && data.userId) {
                    setEditedData(prev => ({ ...prev, userId: data.userId }));
                }
            }
        }
    };
    fetchStallDetails();
  }, [stall.id]);

  const fetchListings = async () => {
    if (stall.id && !listingsLoaded) {
      try {
        setMediaLoading(true);
        setError(null);
        const listingsCollectionRef = collection(db, 'stalls', stall.id, 'listings');
        const listingsSnapshot = await getDocs(listingsCollectionRef);
        const fetchedListings = listingsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as StallListing[];
        setListings(fetchedListings);
        setListingsLoaded(true);
      } catch (err) {
        console.error('Error fetching listings:', err);
        setError('Failed to load listings.');
      } finally {
        setMediaLoading(false);
      }
    }
  };

  const handleToggleListings = () => {
    if (!showListings && !listingsLoaded) {
      fetchListings();
    }
    setShowListings(!showListings);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedData(prevData => {
      const newState = { ...prevData, [name]: value };
      if (name === 'selectedCountry') {
        newState.selectedRegion = '';
        newState.selectedDistrict = '';
        newState.selectedSubcounty = '';
        newState.selectedParish = '';
        newState.selectedVillage = '';
        setRegions([]);
        setDistricts([]);
        setSubcounties([]);
        setParishes([]);
        setVillages([]);
      }
      if (name === 'selectedRegion') {
        newState.selectedDistrict = '';
        newState.selectedSubcounty = '';
        newState.selectedParish = '';
        newState.selectedVillage = '';
        setDistricts([]);
        setSubcounties([]);
        setParishes([]);
        setVillages([]);
      }
      if (name === 'selectedDistrict') {
        newState.selectedSubcounty = '';
        newState.selectedParish = '';
        newState.selectedVillage = '';
        setSubcounties([]);
        setParishes([]);
        setVillages([]);
      }
      if (name === 'selectedSubcounty') {
        newState.selectedParish = '';
        newState.selectedVillage = '';
        setParishes([]);
        setVillages([]);
      }
      if (name === 'selectedParish') {
        newState.selectedVillage = '';
        setVillages([]);
      }
      return newState;
    });
  };
  
  // 🆕 Function to handle changes for a specific listing field (title, description, price)
  const handleListingChange = (listingId: string, name: keyof StallListing, value: string | number) => {
    setListings(prevListings =>
        prevListings.map(listing => {
            if (listing.id === listingId) {
                // Special handling for 'price' to convert string to number/null
                let finalValue: string | number | null = value;
                if (name === 'price') {
                    // Convert to number, or null if empty string
                    const numValue = value === '' ? null : Number(value);
                    // Check if conversion resulted in a valid number (or null)
                    finalValue = (numValue === null || !isNaN(numValue)) ? numValue : listing.price;
                }
                return { ...listing, [name]: finalValue };
            }
            return listing;
        })
    );
  };

  // 🆕 Handle Profile Photo File Change
  const handleProfilePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file for the profile photo.');
        setSelectedProfilePhoto(null);
        return;
      }
      if (file.size > MAX_PROFILE_PHOTO_SIZE) {
        setError(`Profile photo must be less than ${MAX_PROFILE_PHOTO_SIZE / (1024 * 1024)}MB.`);
        setSelectedProfilePhoto(null);
        return;
      }
      setError(null);
      setSelectedProfilePhoto(file);
    }
  };

  // 🆕 Function to remove the *newly selected* photo before upload
  const handleRemoveSelectedPhoto = () => {
    setSelectedProfilePhoto(null);
    setError(null);
  };
  
  // 🆕 Function to remove the *current* photo from the database
  const handleRemoveExistingPhoto = async () => {
      // Replace browser confirm with a custom confirmation logic or callback
      const shouldDelete = window.confirm(`Are you sure you want to remove the current profile photo?`);
      if (!shouldDelete) {
        return;
      }
      
      setLoading(true);
      setError(null);
      const oldUrl = editedData.businessPhotoPreview;
      
      try {
        if (oldUrl) {
          const storage = getStorage();
          const filePath = getStoragePathFromUrl(oldUrl);
          if (filePath) {
            const fileRef = ref(storage, filePath);
            await deleteObject(fileRef);
          }
        }
        
        // Update Firestore
        const stallDocRef = doc(db, 'stalls', stall.id);
        await updateDoc(stallDocRef, {
            businessPhotoPreview: '', // Set to empty string
        });
        
        // Update local state
        setEditedData(prev => ({ ...prev, businessPhotoPreview: '' }));
      } catch (err: any) {
        console.error('Error deleting profile photo:', err);
        setError('Failed to remove profile photo: ' + err.message);
      } finally {
        setLoading(false);
      }
  };


  // 🆕 Function to handle the upload logic
  const handleProfilePhotoUpload = async (currentData: Partial<Stall>) => {
    if (!selectedProfilePhoto) {
      return currentData; // No new photo to upload
    }

    const storage = getStorage();
    const oldUrl = currentData.businessPhotoPreview;
    let newPhotoUrl = oldUrl;

    try {
      // 1. Delete old photo if it exists and we're uploading a new one
      if (oldUrl) {
        const filePath = getStoragePathFromUrl(oldUrl);
        if (filePath) {
          const fileRef = ref(storage, filePath);
          await deleteObject(fileRef);
          console.log("Old profile photo deleted successfully.");
        }
      }

      // 2. Upload the new photo
      const fileName = `${stall.id}_profile_${Date.now()}`;
      const storageRef = ref(storage, `stalls/${stall.id}/${fileName}`);

      const uploadResult = await uploadBytes(storageRef, selectedProfilePhoto);
      newPhotoUrl = await getDownloadURL(uploadResult.ref);
      console.log("New profile photo uploaded successfully:", newPhotoUrl);

      // 3. Reset the selected file state
      setSelectedProfilePhoto(null);
      
      return { ...currentData, businessPhotoPreview: newPhotoUrl };
      
    } catch (err: any) {
      // Re-throw the error to be caught by handleSubmit
      console.error('Error during profile photo upload/delete:', err);
      throw new Error('Failed to update profile photo: ' + err.message);
    }
  };


  const getStoragePathFromUrl = (url: string) => {
    try {
      const decodedUrl = decodeURIComponent(url);
      const pathStartIndex = decodedUrl.indexOf('/o/') + 3;
      const pathEndIndex = decodedUrl.indexOf('?alt=media');
      if (pathStartIndex !== -1 && pathEndIndex !== -1) {
        return decodedUrl.substring(pathStartIndex, pathEndIndex);
      }
    } catch (e) {
      console.error("Error decoding URL for path extraction:", e);
    }
    return null;
  };

  const handleDeleteMedia = async (listingId: string, mediaUrl: string, mediaType: 'images' | 'videos') => {
    // Replace browser confirm with a custom confirmation logic or callback
    const shouldDelete = window.confirm(`Are you sure you want to delete this ${mediaType === 'images' ? 'image' : 'video'}?`);
    if (!shouldDelete) {
      return;
    }

    setMediaLoading(true);
    setError(null);

    try {
      const storage = getStorage();
      const filePath = getStoragePathFromUrl(mediaUrl);

      if (filePath) {
        const fileRef = ref(storage, filePath);
        await deleteObject(fileRef);

        const listingDocRef = doc(db, 'stalls', stall.id, 'listings', listingId);
        const currentListing = listings.find(l => l.id === listingId);

        if (currentListing) {
          const updatedMediaArray = currentListing[mediaType].filter(url => url !== mediaUrl);
          await updateDoc(listingDocRef, {
            [mediaType]: updatedMediaArray,
          });

          setListings(prevListings =>
            prevListings.map(l =>
              l.id === listingId ? { ...l, [mediaType]: updatedMediaArray } : l
            )
          );
        }
      } else {
        throw new Error("Could not extract file path from URL.");
      }
    } catch (err) {
      console.error(`Error deleting ${mediaType} from listing ${listingId}:`, err);
      setError(`Failed to delete ${mediaType}. Please try again.`);
    } finally {
      setMediaLoading(false);
    }
  };

  const handleAddMedia = async (listingId: string, file: File, mediaType: 'images' | 'videos') => {
    setMediaLoading(true);
    setError(null);

    try {
      const storage = getStorage();
      const fileName = `${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `listings/${stall.id}/${listingId}/${mediaType}/${fileName}`);

      const uploadResult = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(uploadResult.ref);

      const listingDocRef = doc(db, 'stalls', stall.id, 'listings', listingId);
      const currentListing = listings.find(l => l.id === listingId);

      if (currentListing) {
        const updatedMediaArray = [...currentListing[mediaType], downloadURL];
        await updateDoc(listingDocRef, {
          [mediaType]: updatedMediaArray,
        });

        setListings(prevListings =>
          prevListings.map(l =>
            l.id === listingId ? { ...l, [mediaType]: updatedMediaArray } : l
          )
        );
      }
    } catch (err) {
      console.error(`Error adding ${mediaType} to listing ${listingId}:`, err);
      setError(`Failed to add ${mediaType}. Please try again.`);
    } finally {
      setMediaLoading(false);
    }
  };
  
  // 🆕 Function to save the changes made to a specific listing's title, description, or price
  const handleSaveListingDetails = async (listingId: string) => {
    setMediaLoading(true);
    setError(null);
    const listingToSave = listings.find(l => l.id === listingId);

    if (!listingToSave) {
        setError(`Listing with ID ${listingId} not found.`);
        setMediaLoading(false);
        return;
    }

    try {
        const listingDocRef = doc(db, 'stalls', stall.id, 'listings', listingId);
        // Only update the editable fields: title, description, price
        await updateDoc(listingDocRef, {
            title: listingToSave.title,
            description: listingToSave.description,
            price: listingToSave.price, // Will be null or number
        });
        console.log(`Listing ${listingId} details updated successfully.`);
    } catch (err) {
        console.error(`Error updating listing ${listingId} details:`, err);
        setError(`Failed to save listing details. Please try again.`);
    } finally {
        setMediaLoading(false);
    }
  };

  const handleDeleteListing = async (listingId: string) => {
    if (!window.confirm('Are you sure you want to permanently delete this entire listing and all its media? This action cannot be undone.')) {
      return;
    }

    setMediaLoading(true);
    setError(null);

    try {
      const listingToDelete = listings.find(l => l.id === listingId);
      if (!listingToDelete) {
        throw new Error("Listing not found for deletion.");
      }

      const storage = getStorage();
      const deletePromises: Promise<void>[] = [];

      // Collect all media URLs to delete
      const mediaUrls = [...(listingToDelete.images || []), ...(listingToDelete.videos || [])];
      
      mediaUrls.forEach(url => {
        const path = getStoragePathFromUrl(url);
        if (path) {
          const fileRef = ref(storage, path);
          deletePromises.push(deleteObject(fileRef));
        }
      });

      await Promise.all(deletePromises);

      const listingDocRef = doc(db, 'stalls', stall.id, 'listings', listingId);
      await deleteDoc(listingDocRef);

      setListings(prevListings => prevListings.filter(l => l.id !== listingId));
      alert('Listing deleted successfully.');
    } catch (err: any) {
      console.error(`Error deleting listing ${listingId}:`, err);
      setError(`Failed to delete listing. Please try again. Error: ${err.message}`);
    } finally {
      setMediaLoading(false);
    }
  };

  // --- Add Listing Logic ---

  const handleMediaTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMediaType(e.target.value);
    setItemImageDescription('');
    setItemImagePrice('');
    setVideoDescription('');
    setGeneralImageDescription('');
    setFileInputKey(prevKey => prevKey + 1);
    setCreateListingError(null);
  };

  const handleMediaFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'item_image' | 'video' | 'general_image') => {
    const file = e.target.files ? e.target.files[0] : null;
    if (!file) return;

    if (type === 'item_image') {
        if (!itemImageDescription.trim()) {
            setCreateListingError("Please enter an item description before selecting the image.");
            e.target.value = '';
            return;
        }
        if (!itemImagePrice) {
            setCreateListingError("Please enter a price before selecting the image.");
            e.target.value = '';
            return;
        }
    } else if (type === 'video') {
        if (!videoDescription.trim()) {
            setCreateListingError("Please enter a video description before selecting the video.");
            e.target.value = '';
            return;
        }
    } else if (type === 'general_image') {
        if (!generalImageDescription.trim()) {
            setCreateListingError("Please enter a description before selecting the image.");
            e.target.value = '';
            return;
        }
    }

    const addMedia = (fileToAdd: File) => {
         const newMediaItem: FormMediaItem = {
            id: `${type === 'video' ? 'vid' : 'img'}-${Date.now()}`,
            url: URL.createObjectURL(fileToAdd),
            file: fileToAdd,
            type: type === 'video' ? 'video' : 'image',
            description: type === 'item_image' ? itemImageDescription : (type === 'video' ? videoDescription : generalImageDescription),
            price: type === 'item_image' ? Number(itemImagePrice) : undefined,
        };

        setUploadedMedia(prev => [...prev, newMediaItem]);
        setItemImageDescription('');
        setItemImagePrice('');
        setVideoDescription('');
        setGeneralImageDescription('');
        setFileInputKey(prev => prev + 1);
        setCreateListingError(null);
    }

    const maxImageSize = 10 * 1024 * 1024;
    const maxVideoSize = 100 * 1024 * 1024;
    const isPremium = editedData.stallType === 'Premium';
    const maxImages = isPremium ? 30 : 20;
    const maxVideos = isPremium ? 10 : 3;
    
    if (type === 'video') {
        if (!file.type.startsWith('video/')) {
            setCreateListingError("Please upload a video file (MP4, WebM, or OGG).");
            e.target.value = '';
            return;
        }
        if (file.size > maxVideoSize) {
            setCreateListingError("Video file is too large (max 100MB).");
            e.target.value = '';
            return;
        }
        if (uploadedMedia.filter(m => m.type === 'video').length >= maxVideos) {
             setCreateListingError(`Maximum ${maxVideos} videos reached.`);
             e.target.value = '';
             return;
        }
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = function() {
            URL.revokeObjectURL(video.src);
            if (video.duration > 60) {
                setCreateListingError("Video must be 1 minute or less.");
                e.target.value = '';
            } else {
                addMedia(file);
            }
        };
        video.src = URL.createObjectURL(file);
    } else {
        if (!file.type.startsWith('image/')) {
            setCreateListingError("Please upload an image file (JPEG, PNG, etc.).");
            e.target.value = '';
            return;
        }
        if (file.size > maxImageSize) {
            setCreateListingError("Image file is too large (max 10MB).");
            e.target.value = '';
            return;
        }
        if (uploadedMedia.filter(m => m.type === 'image').length >= maxImages) {
             setCreateListingError(`Maximum ${maxImages} images reached.`);
             e.target.value = '';
             return;
        }
        addMedia(file);
    }
  };

  const handleRemoveNewMedia = (idToRemove: string) => {
    setUploadedMedia(prev => {
        const item = prev.find(item => item.id === idToRemove);
        if (item) URL.revokeObjectURL(item.url);
        return prev.filter(item => item.id !== idToRemove);
    });
  };

  const handleVerificationFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'nid' | 'title_logbook') => {
    const file = e.target.files ? e.target.files[0] : null;
    if (!file) {
        if (type === 'nid') setNidFile(null);
        if (type === 'title_logbook') setTitleOrLogbookFile(null);
        return;
    }
    const maxFileSize = 5 * 1024 * 1024;
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        setCreateListingError("Please upload an image (JPEG, PNG) or PDF file.");
        e.target.value = '';
        return;
    }
    if (file.size > maxFileSize) {
        setCreateListingError("File is too large (max 5MB).");
        e.target.value = '';
        return;
    }
    if (type === 'nid') setNidFile(file);
    else setTitleOrLogbookFile(file);
    setCreateListingError(null);
  };

  const uploadFileToFirebaseStorage = async (file: File, stallId: string, listingId: string, folder: 'images' | 'videos' | 'documents'): Promise<string> => {
    const fileExtension = file.name.split('.').pop();
    const fileName = `${file.name.replace(/\.[^/.]+$/, '')}_${Date.now()}.${fileExtension}`;
    const filePath = `listings/${stallId}/${listingId}/${folder}/${fileName}`;
    const fileRef = ref(getStorage(), filePath);
    const snapshot = await uploadBytes(fileRef, file);
    return await getDownloadURL(snapshot.ref);
  };

  const handleCreateListing = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stall.id || !editedData.category) {
        setCreateListingError("Stall information incomplete.");
        return;
    }
    if (uploadedMedia.length === 0) {
        setCreateListingError("Please add at least one image or video.");
        return;
    }

    setIsCreatingListing(true);
    setCreateListingError(null);

    try {
        const listingsCollectionRef = collection(db, `stalls/${stall.id}/listings`);
        const newListingDocRef = doc(listingsCollectionRef);
        const listingId = newListingDocRef.id;

        const mediaDetails = await Promise.all(uploadedMedia.map(async (mediaItem) => {
            const isVideo = mediaItem.type === 'video';
            const folder = isVideo ? 'videos' : 'images';
            const url = await uploadFileToFirebaseStorage(mediaItem.file, stall.id, listingId, folder);
            return {
                url,
                description: mediaItem.description || '',
                type: isVideo ? 'video' : 'image',
                price: mediaItem.price
            };
        }));

        const requiresVerification = VERIFICATION_CATEGORIES.includes(editedData.category.toLowerCase());
        let verificationDocs = {};
        let status = requiresVerification ? 'pending_verification' : 'active';

        if (requiresVerification) {
            if (stallVerification.status === 'pending' || stallVerification.status === 'verified') {
                verificationDocs = {
                    verificationStatus: stallVerification.status,
                    nidDocument: stallVerification.nidUrl,
                    titleOrLogbookDocument: stallVerification.titleUrl,
                };
                status = stallVerification.status === 'verified' ? 'active' : 'pending_verification';
            } else {
                if (!nidFile || !titleOrLogbookFile) throw new Error("Verification documents required.");
                const [nidUrl, titleUrl] = await Promise.all([
                    uploadFileToFirebaseStorage(nidFile, stall.id, listingId, 'documents'),
                    uploadFileToFirebaseStorage(titleOrLogbookFile, stall.id, listingId, 'documents')
                ]);
                await updateDoc(doc(db, 'stalls', stall.id), {
                    verificationStatus: 'pending',
                    nidDocumentUrl: nidUrl,
                    titleOrLogbookDocumentUrl: titleUrl,
                });
                verificationDocs = { verificationStatus: 'pending', nidDocument: nidUrl, titleOrLogbookDocument: titleUrl, verifiedAt: null, verifiedBy: null };
            }
        }

        const imageUrls = mediaDetails.filter(m => m.type === 'image').map(m => m.url);
        const videoUrls = mediaDetails.filter(m => m.type === 'video').map(m => m.url);
        const firstMediaItem = mediaDetails[0];

        await setDoc(newListingDocRef, {
            listingId,
            title: editedData.businessName || 'Untitled Listing',
            description: firstMediaItem?.description || editedData.additionalDetails || '',
            category: editedData.category,
            contact: editedData.businessPhone || '',
            sold: false,
            images: imageUrls,
            videos: videoUrls,
            mediaDetails,
            userId: editedData.userId || stall.id, // Fallback to stall ID if userId missing
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            price: firstMediaItem?.price || null,
            stallId: stall.id,
            status,
            ...verificationDocs
        });

        setUploadedMedia([]);
        setSelectedMediaType('');
        setNidFile(null);
        setTitleOrLogbookFile(null);
        setFileInputKey(prev => prev + 1);
        setShowAddListing(false);
        fetchListings(); // Refresh list
        alert("Listing created successfully!");
    } catch (err: any) {
        console.error("Error creating listing:", err);
        setCreateListingError(err.message || "Failed to create listing.");
    } finally {
        setIsCreatingListing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // 1. Handle profile photo upload and get the updated data object
      const dataWithPhoto = await handleProfilePhotoUpload(editedData);

      // 2. Save the updated stall data (including the new photo URL if uploaded)
      const stallDocRef = doc(db, 'stalls', stall.id);
      await updateDoc(stallDocRef, dataWithPhoto);
      
      // 3. Optionally, save all *currently displayed* listing details as well.
      // This ensures all textual changes are persisted if the user presses 'Save Changes' on the main form.
      await Promise.all(
        listings.map(listing => {
            const listingDocRef = doc(db, 'stalls', stall.id, 'listings', listing.id);
            return updateDoc(listingDocRef, {
                title: listing.title,
                description: listing.description,
                price: listing.price,
            });
        })
      );
      
      onSave(dataWithPhoto); // Pass the final updated data
    } catch (err: any) {
      console.error('Error updating stall:', err);
      // If handleProfilePhotoUpload threw an error, it's already customized
      setError(err.message || 'Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalContent>
      <ModalHeader>
        <h3>Edit Stall {stall.stallNumber}</h3>
      </ModalHeader>
      {(error || (loading && selectedProfilePhoto)) && (
        <ErrorMessage>{error || 'Uploading profile photo...'}</ErrorMessage>
      )}

      {/* 🆕 Profile Photo Preview and Controls */}
      <ImagePreviewContainer style={{ flexDirection: 'column', gap: '1rem', alignItems: 'flex-start' }}>
        <h4>Current Profile Photo:</h4>
        <div style={{ position: 'relative', width: '200px', height: '150px', border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
            {(selectedProfilePhoto || editedData.businessPhotoPreview) ? (
                <img 
                    src={selectedProfilePhoto ? URL.createObjectURL(selectedProfilePhoto) : editedData.businessPhotoPreview} 
                    alt={`Photo for ${editedData.businessName}`} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
            ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', backgroundColor: '#f0f0f0' }}>No Photo</div>
            )}
        </div>

        <MediaActions style={{ marginTop: '0.5rem', flexDirection: 'column', alignItems: 'flex-start' }}>
            <label>
                Update Photo:
                <AddMediaInput
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePhotoChange}
                    disabled={loading}
                    style={{ marginLeft: '10px' }}
                />
            </label>
            
            {/* Show remove button for the newly selected file */}
            {selectedProfilePhoto && (
                <MediaButton type="button" onClick={handleRemoveSelectedPhoto} style={{ background: '#ffc107', marginTop: '5px' }}>
                    Cancel New Photo
                </MediaButton>
            )}

            {/* Show delete button for the existing photo */}
            {editedData.businessPhotoPreview && !selectedProfilePhoto && (
                <MediaButton type="button" onClick={handleRemoveExistingPhoto} style={{ background: '#dc3545', marginTop: '5px' }}>
                    Remove Current Photo
                </MediaButton>
            )}
        </MediaActions>
      </ImagePreviewContainer>
      {/* 🆕 End Profile Photo Controls */}


      <form onSubmit={handleSubmit}>
        <FormGrid>
          {/* General Business Info */}
          <FormSection>
            <h4>General Information</h4>
            <label>
              <span>Stall Number:</span>
              <input type="text" name="stallNumber" value={editedData.stallNumber || ''} onChange={handleChange} required />
            </label>
            <label>
              <span>Business Name:</span>
              <input type="text" name="businessName" value={editedData.businessName || ''} onChange={handleChange} required />
            </label>
            <label className="block">
          <span className="text-gray-700">Business Type:</span>
          <select
            name="stallType"
            value={editedData.stallType || ''}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            <option value="" disabled>Select an option</option>
            <option value="Standard">Standard</option>
            <option value="Premium">Premium</option>
          </select>
        </label>
            <label>
              <span>Category:</span>
              <select name="category" value={editedData.category || ''} onChange={handleChange} required>
                {stallCategories.map((category, index) => (
                  <option key={index} value={category}>{category}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Additional Details:</span>
              <textarea name="additionalDetails" value={editedData.additionalDetails || ''} onChange={handleChange}></textarea>
            </label>
          </FormSection>

          {/* Contact Information */}
          <FormSection>
            <h4>Contact Details</h4>
            <label>
              <span>Contact Person:</span>
              <input type="text" name="contactPerson" value={editedData.contactPerson || ''} onChange={handleChange} required />
            </label>
            <label>
              <span>Email:</span>
              <input type="email" name="email" value={editedData.email || ''} onChange={handleChange} />
            </label>
            <label>
              <span>Business Phone:</span>
              <input type="text" name="businessPhone" value={editedData.businessPhone || ''} onChange={handleChange} required />
            </label>
            <label>
              <span>WhatsApp Phone:</span>
              <input type="text" name="whatsappPhone" value={editedData.whatsappPhone || ''} onChange={handleChange} />
            </label>
          </FormSection>

          {/* Location Details - now with cascading selects */}
          <FormSection>
            <h4>Location Details</h4>
            <label>
              <span>Actual Location:</span>
              <input type="text" name="actualLocation" value={editedData.actualLocation || ''} onChange={handleChange} required />
            </label>
            <label>
              <span>Google Map Location:</span>
              <input type="text" name="googleMapLocation" value={editedData.googleMapLocation || ''} onChange={handleChange} />
            </label>
            <label>
              <span>Country:</span>
              <select name="selectedCountry" value={editedData.selectedCountry || ''} onChange={handleChange}>
                <option value="">Select a Country</option>
                {countries.map((country, index) => (
                  <option key={index} value={country}>{country}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Region:</span>
              <select name="selectedRegion" value={editedData.selectedRegion || ''} onChange={handleChange} disabled={editedData.selectedCountry !== 'Uganda'}>
                <option value="">Select a Region</option>
                {regions.map(region => (
                  <option key={region.id} value={region.name}>{region.name}</option>
                ))}
              </select>
            </label>
            <label>
              <span>District:</span>
              <select name="selectedDistrict" value={editedData.selectedDistrict || ''} onChange={handleChange} disabled={!editedData.selectedRegion}>
                <option value="">Select a District</option>
                {districts.map(district => (
                  <option key={district.id} value={district.name}>{district.name}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Subcounty:</span>
              <select name="selectedSubcounty" value={editedData.selectedSubcounty || ''} onChange={handleChange} disabled={!editedData.selectedDistrict}>
                <option value="">Select a Subcounty</option>
                {subcounties.map(subcounty => (
                  <option key={subcounty.id} value={subcounty.name}>{subcounty.name}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Parish:</span>
              <select name="selectedParish" value={editedData.selectedParish || ''} onChange={handleChange} disabled={!editedData.selectedSubcounty}>
                <option value="">Select a Parish</option>
                {parishes.map(parish => (
                  <option key={parish.id} value={parish.name}>{parish.name}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Village:</span>
              <select name="selectedVillage" value={editedData.selectedVillage || ''} onChange={handleChange} disabled={!editedData.selectedParish}>
                <option value="">Select a Village</option>
                {villages.map((village, index) => (
                  <option key={index} value={village}>{village}</option>
                ))}
              </select>
            </label>
          </FormSection>

          {/* Social Links & Website */}
          <FormSection>
            <h4>Social & Website</h4>
            <label>
              <span>Website:</span>
              <input type="text" name="website" value={editedData.website || ''} onChange={handleChange} />
            </label>
            <label>
              <span>Facebook:</span>
              <input type="text" name="facebookLink" value={editedData.facebookLink || ''} onChange={handleChange} />
            </label>
            <label>
              <span>Instagram:</span>
              <input type="text" name="instagramLink" value={editedData.instagramLink || ''} onChange={handleChange} />
            </label>
            <label>
              <span>X/Twitter:</span>
              <input type="text" name="xTwitterLink" value={editedData.xTwitterLink || ''} onChange={handleChange} />
            </label>
            <label>
              <span>LinkedIn:</span>
              <input type="text" name="linkedinLink" value={editedData.linkedinLink || ''} onChange={handleChange} />
            </label>
            <label>
              <span>TikTok:</span>
              <input type="text" name="tiktokLink" value={editedData.tiktokLink || ''} onChange={handleChange} />
            </label>
            <label>
              <span>YouTube:</span>
              <input type="text" name="youtubeLink" value={editedData.youtubeLink || ''} onChange={handleChange} />
            </label>
          </FormSection>
        </FormGrid>

        {/* Listings Section with Edit/Delete Media */}
        <FormSection>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4>Stall Listings</h4>
            <button
              type="button"
              onClick={handleToggleListings}
              style={{
                padding: '0.5rem 1rem',
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              {showListings ? 'Hide Listings' : 'Show Listings'}
            </button>
          </div>
          
          {/* Add New Listing Toggle */}
          <div style={{ marginBottom: '1rem' }}>
            <button
                type="button"
                onClick={() => setShowAddListing(!showAddListing)}
                style={{
                    padding: '0.5rem 1rem',
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                }}
            >
                {showAddListing ? 'Cancel Adding Listing' : 'Add New Listing'}
            </button>
          </div>

          {/* Add New Listing Form */}
          {showAddListing && (
            <div style={{ border: '1px solid #28a745', padding: '1rem', borderRadius: '8px', marginBottom: '2rem', backgroundColor: '#f9fff9' }}>
                <h5>Create New Listing</h5>
                {createListingError && <ErrorMessage>{createListingError}</ErrorMessage>}
                
                <label style={{ display: 'block', marginBottom: '10px' }}>
                    <span>Select Media Type:</span>
                    <select value={selectedMediaType} onChange={handleMediaTypeChange} disabled={isCreatingListing} style={{ width: '100%', padding: '8px', marginTop: '5px' }}>
                        {mediaTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                    </select>
                </label>

                {selectedMediaType === 'item_image' && (
                    <>
                        <label style={{ display: 'block', marginBottom: '10px' }}>
                            <span>Item Description:</span>
                            <input type="text" value={itemImageDescription} onChange={e => setItemImageDescription(e.target.value)} placeholder="e.g., Brand new" disabled={isCreatingListing} style={{ width: '100%', padding: '8px', marginTop: '5px' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: '10px' }}>
                            <span>Price (Ugx):</span>
                            <input type="number" value={itemImagePrice} onChange={e => setItemImagePrice(e.target.value)} placeholder="e.g., 1500000" disabled={isCreatingListing} style={{ width: '100%', padding: '8px', marginTop: '5px' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: '10px' }}>
                            <span>Item Image:</span>
                            <AddMediaInput type="file" accept="image/*" onChange={e => handleMediaFileChange(e, 'item_image')} key={`item_image-${fileInputKey}`} disabled={isCreatingListing} />
                        </label>
                    </>
                )}

                {selectedMediaType === 'video' && (
                    <>
                        <label style={{ display: 'block', marginBottom: '10px' }}>
                            <span>Video Description:</span>
                            <input type="text" value={videoDescription} onChange={e => setVideoDescription(e.target.value)} placeholder="e.g., Features" disabled={isCreatingListing} style={{ width: '100%', padding: '8px', marginTop: '5px' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: '10px' }}>
                            <span>Video (Max 1 min, 100MB):</span>
                            <AddMediaInput type="file" accept="video/mp4,video/webm,video/ogg" onChange={e => handleMediaFileChange(e, 'video')} key={`video-${fileInputKey}`} disabled={isCreatingListing} />
                        </label>
                    </>
                )}

                {selectedMediaType === 'general_image' && (
                    <>
                        <label style={{ display: 'block', marginBottom: '10px' }}>
                            <span>Image Description:</span>
                            <input type="text" value={generalImageDescription} onChange={e => setGeneralImageDescription(e.target.value)} placeholder="e.g., Angle" disabled={isCreatingListing} style={{ width: '100%', padding: '8px', marginTop: '5px' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: '10px' }}>
                            <span>General Image:</span>
                            <AddMediaInput type="file" accept="image/*" onChange={e => handleMediaFileChange(e, 'general_image')} key={`general_image-${fileInputKey}`} disabled={isCreatingListing} />
                        </label>
                    </>
                )}

                {uploadedMedia.length > 0 && (
                    <ImagePreviewContainer style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                        <h6>Media to be uploaded ({uploadedMedia.length})</h6>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                            {uploadedMedia.map(media => (
                                <div key={media.id} style={{ border: '1px solid #ddd', padding: '5px', borderRadius: '4px', position: 'relative' }}>
                                    {media.type === 'image' ? (
                                        <img src={media.url} alt="Preview" style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
                                    ) : (
                                        <video src={media.url} style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
                                    )}
                                    <div style={{ fontSize: '0.8rem', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{media.description}</div>
                                    <button type="button" onClick={() => handleRemoveNewMedia(media.id)} style={{ position: 'absolute', top: 0, right: 0, background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer' }}>&times;</button>
                                </div>
                            ))}
                        </div>
                    </ImagePreviewContainer>
                )}

                {/* Verification Section */}
                {VERIFICATION_CATEGORIES.includes(editedData.category?.toLowerCase() || '') && stallVerification.status === null && (
                    <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                        <h6 style={{ color: '#e74c3c' }}>Verification Required</h6>
                        <label style={{ display: 'block', marginBottom: '10px' }}>
                            <span>National ID (NID):</span>
                            <AddMediaInput type="file" accept="image/*,.pdf" onChange={e => handleVerificationFileChange(e, 'nid')} required disabled={isCreatingListing} />
                            {nidFile && <span style={{ fontSize: '0.8rem' }}>{nidFile.name}</span>}
                        </label>
                        <label style={{ display: 'block', marginBottom: '10px' }}>
                            <span>{editedData.category?.toLowerCase() === 'cars' ? 'Logbook' : 'Title Deed'}:</span>
                            <AddMediaInput type="file" accept="image/*,.pdf" onChange={e => handleVerificationFileChange(e, 'title_logbook')} required disabled={isCreatingListing} />
                            {titleOrLogbookFile && <span style={{ fontSize: '0.8rem' }}>{titleOrLogbookFile.name}</span>}
                        </label>
                    </div>
                )}

                <MediaButton 
                    type="button" 
                    onClick={handleCreateListing} 
                    disabled={isCreatingListing || uploadedMedia.length === 0}
                    style={{ marginTop: '15px', width: '100%', background: '#007bff' }}
                >
                    {isCreatingListing ? 'Creating Listing...' : 'Save New Listing'}
                </MediaButton>
            </div>
          )}

          {showListings && (
            <>
              {mediaLoading && !listingsLoaded ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <LoadingSpinner />
                  <p>Loading listings...</p>
                </div>
              ) : listings.length > 0 ? (
                listings.map((listing) => (
              <div key={listing.id} style={{ marginBottom: '2rem', border: '1px solid #ccc', padding: '1rem', borderRadius: '8px', background: '#fdfdfd', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>

                {/* Editable Title */}
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                    <strong>Title:</strong>
                    <input 
                        type="text" 
                        name="title" 
                        value={listing.title || ''} 
                        onChange={(e) => handleListingChange(listing.id, 'title', e.target.value)} 
                        style={{ width: '100%', padding: '8px', marginTop: '5px', boxSizing: 'border-box' }}
                    />
                </label>
                
                {/* Editable Description */}
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                    <strong>Description:</strong>
                    <textarea 
                        name="description" 
                        value={listing.description || ''} 
                        onChange={(e) => handleListingChange(listing.id, 'description', e.target.value)} 
                        rows={3}
                        style={{ width: '100%', padding: '8px', marginTop: '5px', boxSizing: 'border-box' }}
                    ></textarea>
                </label>
                
                {/* Editable Price */}
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                    <strong>Price:</strong>
                    <input 
                        type="number" 
                        name="price" 
                        value={listing.price === null ? '' : listing.price} 
                        onChange={(e) => handleListingChange(listing.id, 'price', e.target.value)} 
                        placeholder="N/A or a number"
                        style={{ width: '100%', padding: '8px', marginTop: '5px', boxSizing: 'border-box' }}
                    />
                </label>
                
                {/* Save Listing Details Button */}
                <MediaButton 
                    type="button" 
                    onClick={() => handleSaveListingDetails(listing.id)} 
                    disabled={mediaLoading}
                    style={{ background: '#28a745', color: 'white', marginTop: '10px', marginBottom: '15px', padding: '8px 15px' }}
                >
                    {mediaLoading ? 'Saving...' : 'Save Listing Details'}
                </MediaButton>

                {/* Images */}
                {listing.images && listing.images.length > 0 && (
                  <ImagePreviewContainer>
                    <h5>Images:</h5>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                      {listing.images.map((imageUrl, index) => (
                        <div key={index} style={{ border: '1px solid #ddd', padding: '0.5rem', borderRadius: '4px' }}>
                          <img src={imageUrl} alt={`Listing image ${index + 1}`} style={{ maxWidth: '150px', height: 'auto', display: 'block' }} />
                          <MediaActions>
                            <MediaButton
                              type="button"
                              onClick={() => handleDeleteMedia(listing.id, imageUrl, 'images')}
                              disabled={mediaLoading}
                            >
                              {mediaLoading ? 'Deleting...' : 'Delete Image'}
                            </MediaButton>
                          </MediaActions>
                        </div>
                      ))}
                    </div>
                  </ImagePreviewContainer>
                )}
                <label>
                  Add Image:
                  <AddMediaInput
                    type="file"
                    accept="image/*"
                    onChange={(e) => e.target.files && handleAddMedia(listing.id, e.target.files[0], 'images')}
                    disabled={mediaLoading}
                  />
                </label>

                {/* Videos */}
                {listing.videos && listing.videos.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <h5>Videos:</h5>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                      {listing.videos.map((videoUrl, index) => (
                        <div key={index} style={{ border: '1px solid #ddd', padding: '0.5rem', borderRadius: '4px' }}>
                          <video controls style={{ maxWidth: '150px', height: 'auto', display: 'block' }}>
                            <source src={videoUrl} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                          <MediaActions>
                            <MediaButton
                              type="button"
                              onClick={() => handleDeleteMedia(listing.id, videoUrl, 'videos')}
                              disabled={mediaLoading}
                            >
                              {mediaLoading ? 'Deleting...' : 'Delete Video'}
                            </MediaButton>
                          </MediaActions>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                 <label>
                  Add Video:
                  <AddMediaInput
                    type="file"
                    accept="video/*"
                    onChange={(e) => e.target.files && handleAddMedia(listing.id, e.target.files[0], 'videos')}
                    disabled={mediaLoading}
                  />
                </label>

                {/* Delete Listing Button */}
                <div style={{ marginTop: '2rem', borderTop: '1px solid #e0e0e0', paddingTop: '1rem' }}>
                    <MediaButton
                        type="button"
                        onClick={() => handleDeleteListing(listing.id)}
                        disabled={mediaLoading}
                        style={{ background: '#dc3545', color: 'white', width: '100%', fontSize: '1rem' }}
                    >
                        {mediaLoading ? 'Deleting...' : 'Delete Entire Listing'}
                    </MediaButton>
                </div>
              </div>
                ))
              ) : (
                <p>No listings found for this stall.</p>
              )}
            </>
          )}
        </FormSection>

        <ModalActions>
          <CancelButton type="button" onClick={onCancel}>
            Cancel
          </CancelButton>
          <ApproveButton type="submit" disabled={loading}>
            {loading ? <LoadingSpinner /> : 'Save Changes'}
          </ApproveButton>
        </ModalActions>
      </form>
    </ModalContent>
  );
};

export default EditStallForm;